import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowRight, BookOpen } from "@phosphor-icons/react";
import { GoogleButton } from "../components/GoogleButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      nav(location.state?.from || "/dashboard", { replace: true });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="login-logo-link" className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-12 h-12 rounded-2xl neu-sm flex items-center justify-center text-indigo">
            <BookOpen size={22} weight="duotone" />
          </div>
          <div className="text-xl font-bold tracking-tight">Voxlingua</div>
        </Link>

        <form onSubmit={onSubmit} className="neu rounded-3xl p-10" data-testid="login-form">
          <div className="overline mb-3">Log in</div>
          <h1 className="text-3xl md:text-4xl text-foreground font-bold tracking-tight leading-tight">Welcome back.</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Don&apos;t have an account?{" "}
            <Link to="/register" data-testid="login-goto-register" className="font-bold text-foreground underline decoration-signal decoration-2 underline-offset-4 hover:text-signal">
              Create one
            </Link>
          </p>

          <div className="mt-8 space-y-5">
            <div>
              <label className="overline mb-2 block" htmlFor="email">Email</label>
              <input
                id="email" type="email" required autoComplete="email"
                data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="neu-input w-full px-5 py-3.5 text-sm"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="overline mb-2 block" htmlFor="password">Password</label>
              <input
                id="password" type="password" required autoComplete="current-password"
                data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="neu-input w-full px-5 py-3.5 text-sm"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="mt-5 text-sm text-red-500 neu-inset-sm px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="neu-btn-primary mt-8 w-full inline-flex items-center justify-center gap-2 px-8 py-4 font-medium"
          >
            {loading ? "Signing in…" : "Sign in"}
            {!loading && <ArrowRight size={16} weight="bold" />}
          </button>
          <GoogleButton testId="login-google-btn" />
        </form>
      </div>
    </div>
  );
}
